import styled from 'styled-components';

import { breakpoints } from 'theme';

import { Badge, TextLink } from '../styled';

/** Shared spacing for table rows and mobile invoice cards. */
const rowPadY = '14px';
const rowPadX = '12px';

export const SectionHeaderBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export const InvoiceTable = styled.table`
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
  font-size: 14px;
  text-align: left;
  color: ${({ theme }) => theme.color.pickledBluewood};

  @media ${breakpoints.downMd} {
    min-width: 560px;
  }
`;

export const Th = styled.th`
  font-family: 'Open Sans', sans-serif;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.color.baliHai};
  padding: 0 ${rowPadX} ${rowPadY};
  border-bottom: 1px solid ${({ theme }) => theme.color.porcelain};
  white-space: nowrap;
  text-align: left;
  vertical-align: bottom;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
    text-align: right;
  }
`;

export const Td = styled.td<{ $alignRight?: boolean; $showDivider?: boolean }>`
  padding: ${rowPadY} ${rowPadX};
  vertical-align: middle;
  border-bottom: ${({ $showDivider, theme }) =>
    $showDivider ? `1px solid ${theme.color.porcelain}` : 'none'};
  color: ${({ theme }) => theme.color.shark};

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
    text-align: ${({ $alignRight }) => ($alignRight ? 'right' : 'left')};
  }
`;

export const InvoicePrimary = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.shark};
`;

export const InvoiceMeta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.baliHai};
  font-weight: 500;
  line-height: 1.4;
`;

export const AmountCell = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${({ theme }) => theme.color.shark};
`;

export const StatusBadgeOpen = styled(Badge)`
  background: #fff8ed;
  color: #a06a0a;
`;

export const DownloadStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
`;

export const DownloadLink = styled(TextLink)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
`;

export const DownloadUnavailable = styled(DownloadLink).attrs({
  as: 'span',
})`
  color: ${({ theme }) => theme.color.baliHai};
  opacity: 0.55;
  cursor: default;
  pointer-events: none;

  &:hover {
    text-decoration: none;
  }
`;

export const EmptyIconCell = styled.td`
  height: 240px;
  vertical-align: middle;
  text-align: center;
  border-bottom: none !important;
  padding: 32px 12px !important;
`;

export const EmptyIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  width: 100%;
  filter: grayscale(1);
  opacity: 0.45;
  color: ${({ theme }) => theme.color.baliHai};

  svg {
    width: 64px;
    height: 64px;
    display: block;
  }
`;

export const SkeletonBar = styled.span<{ $width?: string; $height?: string }>`
  display: inline-block;
  width: ${({ $width }) => $width ?? '72%'};
  height: ${({ $height }) => $height ?? '14px'};
  vertical-align: middle;
`;

export const MobileList = styled.div`
  display: none;
  flex-direction: column;
  gap: 12px;

  @media ${breakpoints.downMdPlus} {
    display: flex;
  }
`;

export const MobileInvoiceCard = styled.div`
  border: 1px solid ${({ theme }) => theme.color.porcelain};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${({ theme }) => theme.color.dew};
`;

export const MobileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

export const MobileLabel = styled.span`
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const MobileDownloads = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
`;

export const DesktopTableWrap = styled.div`
  @media ${breakpoints.downMdPlus} {
    display: none;
  }
`;

/** Keeps billing history height stable across pages (see INVOICES_PER_PAGE). */
export const ListViewport = styled.div`
  min-height: 300px;

  @media ${breakpoints.downMdPlus} {
    min-height: 740px;
  }
`;

export const PaginationFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  min-height: 48px;
  margin-top: 0;
  padding-top: ${rowPadY};
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
`;

export const PaginationSummary = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const PageNav = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

export const PageNavButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  outline: none;
  border: 1px solid ${({ theme }) => theme.color.pickledBluewood};
  border-radius: 6px;
  background: ${({ theme }) => theme.color.white};
  color: ${({ theme }) => theme.color.pickledBluewood};
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(48, 70, 89, 0.08);
  transition: border-color 0.2s, color 0.2s, background 0.2s, box-shadow 0.2s;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.color.brightTurquoise};
    color: ${({ theme }) => theme.color.skobeloffGreen};
    background: ${({ theme }) => theme.color.aquaSqueeze};
    box-shadow: 0 2px 6px rgba(48, 70, 89, 0.12);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow: none;
  }

  &:focus-visible:not(:disabled) {
    outline: 2px solid ${({ theme }) => theme.color.brightTurquoise};
    outline-offset: 2px;
  }

  &:disabled {
    border-color: ${({ theme }) => theme.color.porcelain};
    background: ${({ theme }) => theme.color.dew};
    color: ${({ theme }) => theme.color.casper};
    cursor: not-allowed;
    box-shadow: none;
    opacity: 0.55;
  }
`;

export const EmptyTableRow = styled.tr`
  pointer-events: none;

  td {
    border: none !important;
    padding: ${rowPadY} ${rowPadX};
    line-height: 0;
    font-size: 0;
    color: transparent;
    user-select: none;

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }
  }
`;

export const MobileInvoiceCardPlaceholder = styled.div`
  min-height: 132px;
  flex-shrink: 0;
  pointer-events: none;
`;
